import { z } from "zod";

// Attribute 타입 (Backend: ENUM, NUMBER, SIZE, GRAMMAGE, STRING)
export const AttributeTypeSchema = z.enum(["ENUM", "SIZE", "NUMBER", "GRAMMAGE", "STRING"]);
export type AttributeType = z.infer<typeof AttributeTypeSchema>;

// PreviewType (Backend: SELECT_BOX, SELECT_BOX_WITH_IMAGE, DROP_DOWN_MENU, INPUT_BOX)
export const PreviewTypeSchema = z.enum(["SELECT_BOX", "SELECT_BOX_WITH_IMAGE", "DROP_DOWN_MENU", "INPUT_BOX"]);
export type PreviewType = z.infer<typeof PreviewTypeSchema>;

// PriceType: PER_QUANTITY(개수당 가격), PER_AREA(면적당 가격)
export const PriceTypeSchema = z.enum(["PER_QUANTITY", "PER_AREA"]);
export type PriceType = z.infer<typeof PriceTypeSchema>;

// Value (속성값) 스키마
export const OptionValueDtoSchema = z.object({
  id: z.number().optional(),
  value: z.string(),
  description: z.string().nullable().optional(),
  displayOrder: z.number(),
  width: z.number().nullable().optional(),
  height: z.number().nullable().optional(),
  numberValue: z.number().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  materialId: z.number().nullable().optional(),
  sheetMaterialDetailId: z.number().nullable().optional(),
  rollMaterialDetailId: z.number().nullable().optional(),
  customMaterialDetailId: z.number().nullable().optional(),
});
export type OptionValueDto = z.infer<typeof OptionValueDtoSchema>;

// Attribute (속성) 스키마
export const OptionAttributeDtoSchema = z.object({
  id: z.number().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  attributeType: AttributeTypeSchema,
  previewType: PreviewTypeSchema,
  displayOrder: z.number(),
  required: z.boolean().default(true), // 필수 여부 (기본값: true)
  parentValueId: z.number().nullable().optional(),
  values: z.array(OptionValueDtoSchema),
});
export type OptionAttributeDto = z.infer<typeof OptionAttributeDtoSchema>;

// PriceTier (가격 구간) 스키마
// - PER_QUANTITY: price × 수량
// - PER_AREA: price × (실제면적mm² / baseAreaSize)
export const OptionPriceTierDtoSchema = z.object({
  id: z.number().optional(),
  combinationKeyId: z.number().optional(),
  priceType: PriceTypeSchema,
  minQuantity: z.number().nullable().optional(),
  maxQuantity: z.number().nullable().optional(),
  price: z.number().nullable().optional(),
  areaMultiplier: z.number().nullable().optional(), // 면적 승수 (PER_AREA 전용)
  baseAreaSize: z.number().nullable().optional(), // 기준 면적 크기 (mm² 단위, PER_AREA 전용, 기본값: 1,000,000 = 1m²)
});
export type OptionPriceTierDto = z.infer<typeof OptionPriceTierDtoSchema>;

// CombinationKey (조합키) 스키마
export const OptionCombinationKeyDtoSchema = z.object({
  id: z.number().optional(),
  combinationId: z.number().optional(),
  combinationKey: z.string(),
  priceTiers: z.array(OptionPriceTierDtoSchema),
});
export type OptionCombinationKeyDto = z.infer<typeof OptionCombinationKeyDtoSchema>;

// Combination (조합) 스키마
export const OptionCombinationDtoSchema = z.object({
  id: z.number().optional(),
  optionId: z.number().optional(),
  name: z.string().nullable().optional(),
  attributeIds: z.array(z.number()),
  excludedAttributeIds: z.array(z.number()),
  applyWasteRate: z.boolean(),
  combinationKeys: z.array(OptionCombinationKeyDtoSchema),
});
export type OptionCombinationDto = z.infer<typeof OptionCombinationDtoSchema>;

// Option 상세 응답 스키마
export const OptionDetailResponseSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable().optional(),
  attributes: z.array(OptionAttributeDtoSchema),
  combinations: z.array(OptionCombinationDtoSchema),
});
export type OptionDetailResponse = z.infer<typeof OptionDetailResponseSchema>;

// Option 목록 응답 스키마
export const OptionListResponseSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable().optional(),
});
export type OptionListResponse = z.infer<typeof OptionListResponseSchema>;

// Option 생성 요청 스키마
export const CreateOptionAttributeValueRequestSchema = z.object({
  value: z.string(),
  description: z.string().nullable().optional(),
  displayOrder: z.number(),
  width: z.number().nullable().optional(),
  height: z.number().nullable().optional(),
  numberValue: z.number().nullable().optional(),
  materialId: z.number().nullable().optional(),
  sheetMaterialDetailId: z.number().nullable().optional(),
  rollMaterialDetailId: z.number().nullable().optional(),
  customMaterialDetailId: z.number().nullable().optional(),
});
export type CreateOptionAttributeValueRequest = z.infer<typeof CreateOptionAttributeValueRequestSchema>;

export const CreateOptionAttributeRequestSchema = z.object({
  name: z.string(),
  description: z.string().nullable().optional(),
  attributeType: AttributeTypeSchema,
  previewType: PreviewTypeSchema,
  displayOrder: z.number(),
  required: z.boolean().default(true), // 필수 여부 (기본값: true)
  parentValueId: z.number().nullable().optional(),
  values: z.array(CreateOptionAttributeValueRequestSchema),
});
export type CreateOptionAttributeRequest = z.infer<typeof CreateOptionAttributeRequestSchema>;

// PriceRange 요청 스키마 (옵션 생성 시 필수)
export const CreateOptionPriceRangeRequestSchema = z.object({
  minQuantity: z.number(),
  maxQuantity: z.number().nullable().optional(),
  price: z.number(),
});
export type CreateOptionPriceRangeRequest = z.infer<typeof CreateOptionPriceRangeRequestSchema>;

export const CreateOptionRequestSchema = z.object({
  name: z.string(),
  description: z.string().nullable().optional(),
  items: z.array(CreateOptionAttributeRequestSchema),
  priceRanges: z.array(CreateOptionPriceRangeRequestSchema),
});
export type CreateOptionRequest = z.infer<typeof CreateOptionRequestSchema>;

// Option 수정 요청 스키마
export const UpdateOptionRequestSchema = z.object({
  name: z.string(),
  description: z.string().nullable().optional(),
});
export type UpdateOptionRequest = z.infer<typeof UpdateOptionRequestSchema>;

// Combination 생성 요청 스키마
export const CreateCombinationRequestSchema = z.object({
  name: z.string().nullable().optional(),
  attributeIds: z.array(z.number()),
  excludedAttributeIds: z.array(z.number()),
  applyWasteRate: z.boolean(),
});
export type CreateCombinationRequest = z.infer<typeof CreateCombinationRequestSchema>;

// PriceTier 생성 요청 스키마
export const CreatePriceTierRequestSchema = z.object({
  priceType: PriceTypeSchema,
  minQuantity: z.number().nullable().optional(),
  maxQuantity: z.number().nullable().optional(),
  price: z.number().nullable().optional(),
  areaMultiplier: z.number().nullable().optional(), // PER_AREA 전용: 면적 승수
  baseAreaSize: z.number().nullable().optional(), // PER_AREA 전용: 기준 면적 크기 (mm² 단위, 기본값: 1,000,000 = 1m²)
});
export type CreatePriceTierRequest = z.infer<typeof CreatePriceTierRequestSchema>;

// 레거시 호환성을 위한 타입들 (deprecated)
/** @deprecated Use OptionDetailResponse instead */
export const OptionDtoSchema = OptionDetailResponseSchema;
/** @deprecated Use OptionDetailResponse instead */
export type OptionDto = OptionDetailResponse;

/** @deprecated */
export const QuantityApplyTypeSchema = z.enum([
  "SINGLE",
  "SELECTABLE",
  "SAME_AS_PRODUCT_QUANTITY",
]);
/** @deprecated */
export type QuantityApplyType = z.infer<typeof QuantityApplyTypeSchema>;
